import { Component } from '@nicolas-riciotti/light2';

/**
 * <manual-rail> — the Manual's fixed left tool rail.
 *
 * Download PDF is a plain link. The other controls are disclosures, wired
 * generically off `[aria-expanded]` so adding another one needs no JS:
 *
 *  - Toggles `is-open` on the owning item, which drives the chevron rotation,
 *    the shared border between button and panel, and the 0fr -> 1fr grid-row
 *    reveal. No height is ever measured, so the reveal collapses to instant
 *    when accessibility mode zeroes transition durations.
 *  - Only one panel is open at a time; the rail is narrow and two open lists
 *    would overlap.
 *  - Closes on Escape and on a click outside.
 *  - Marks the Translate language matching the current path with
 *    aria-current="page".
 *
 * Read Aloud narration is the audio plugin's job. This only opens and closes
 * the control and leaves the language radios alone for the plugin to read via
 * `[data-manual-readaloud-lang]`.
 */
export default class ManualRail extends Component {
  attached() {
    this.toggles = Array.from(this.querySelectorAll('[aria-expanded]'));

    if (this.toggles.length) {
      this._onToggle = (e) => {
        const btn = e.currentTarget;
        const item = btn.closest('li');
        if (!item) return;
        e.stopPropagation();
        this.setOpen(item, !item.classList.contains('is-open'));
      };
      this.toggles.forEach((btn) => btn.addEventListener('click', this._onToggle));

      this._onDocClick = (e) => {
        if (!this.contains(e.target)) this.closeAll();
      };
      this._onKey = (e) => {
        if (e.key !== 'Escape') return;
        const open = this.querySelector('li.is-open');
        if (!open) return;
        this.closeAll();
        open.querySelector('[aria-expanded]')?.focus();
      };
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKey);
    }

    // Mark the current language. Compare paths only — a translation may live on
    // another host, and a trailing slash should not decide the match.
    const here = this.normalise(window.location.pathname);
    this.querySelectorAll('[data-manual-translate-link]').forEach((link) => {
      let path;
      try {
        path = this.normalise(new URL(link.href, window.location.origin).pathname);
      } catch (e) {
        return;
      }
      if (path === here) link.setAttribute('aria-current', 'page');
    });
  }

  normalise(pathname) {
    return (pathname || '/').replace(/\/+$/, '') || '/';
  }

  setOpen(item, open) {
    // One at a time: the rail gutter is too narrow for two open lists.
    if (open) this.closeAll();
    item.classList[open ? 'add' : 'remove']('is-open');
    item.querySelector('[aria-expanded]')?.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  closeAll() {
    this.querySelectorAll('li.is-open').forEach((item) => {
      item.classList.remove('is-open');
      item.querySelector('[aria-expanded]')?.setAttribute('aria-expanded', 'false');
    });
  }

  detached() {
    this.toggles?.forEach((btn) => btn.removeEventListener('click', this._onToggle));
    if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
    if (this._onKey) document.removeEventListener('keydown', this._onKey);
  }
}

customElements.define('manual-rail', ManualRail);
